import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, PencilSimple, Baby, CalendarBlank, CheckSquare, Pill, Gift, BellRinging, ChartBar } from '@phosphor-icons/react';
import { KidProfileDialog } from '@/components/kids/KidProfileDialog';
import { OverviewTab } from '@/components/kids/OverviewTab';
import { TimetableTab } from '@/components/kids/TimetableTab';
import { TasksTab } from '@/components/kids/TasksTab';
import { MedsTab } from '@/components/kids/MedsTab';
import { RewardsTab } from '@/components/kids/RewardsTab';
import { ApprovalsTab } from '@/components/kids/ApprovalsTab';

const TABS = [
  { k: 'overview', l: 'Overview', i: ChartBar },
  { k: 'timetable', l: 'Timetable', i: CalendarBlank },
  { k: 'tasks', l: 'Tasks', i: CheckSquare },
  { k: 'medicines', l: 'Medicines', i: Pill },
  { k: 'rewards', l: 'Rewards', i: Gift },
  { k: 'approvals', l: 'Approvals', i: BellRinging },
];

export default function KidsZonePage() {
  const { api } = useAuth();
  const [kids, setKids] = useState([]);
  const [activeKid, setActiveKid] = useState(null);
  const [tab, setTab] = useState('overview');
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [editKid, setEditKid] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchKids = useCallback(async () => {
    try {
      const [k, a] = await Promise.all([api.get('/kids'), api.get('/kids/approvals')]);
      setKids(k.data);
      setPendingCount((a.data.task_logs?.length || 0) + (a.data.redemptions?.length || 0));
      setActiveKid(prev => {
        if (prev) { const found = k.data.find(x => x.kid_id === prev.kid_id); if (found) return found; }
        return k.data[0] || null;
      });
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchKids(); }, [fetchKids]);

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="kids-zone-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="overline">Kids Zone</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Little Champions</h1>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <a href="/kid-mode" target="_blank" rel="noreferrer" data-testid="open-kid-mode-btn"
            className="px-4 py-2 rounded-full border border-border text-sm font-semibold flex items-center gap-2 hover:bg-muted/50">
            <Baby size={16} /> Open Kid Mode
          </a>
          <button onClick={() => { setEditKid(null); setShowProfileDialog(true); }} data-testid="add-kid-btn"
            className="px-4 py-2 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold flex items-center gap-2">
            <Plus size={16} /> Add Kid
          </button>
        </div>
      </div>

      {kids.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Baby size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="font-semibold mb-1">No kid profiles yet</h3>
          <p className="text-sm text-muted-foreground mb-4">Add your first kid to set up schedules, tasks, medicines and rewards.</p>
          <button onClick={() => { setEditKid(null); setShowProfileDialog(true); }} data-testid="add-first-kid-btn"
            className="px-5 py-2.5 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold">Add Kid</button>
        </div>
      ) : (
        <>
          <div className="flex gap-2 flex-wrap items-center">
            {kids.map(k => (
              <button key={k.kid_id} onClick={() => setActiveKid(k)} data-testid={`kid-chip-${k.kid_id}`}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${activeKid?.kid_id === k.kid_id ? 'border-transparent text-white' : 'border-border bg-card text-foreground hover:bg-muted/50'}`}
                style={activeKid?.kid_id === k.kid_id ? { backgroundColor: k.avatar_color || '#FF6B35' } : {}}>
                <span className="text-base">{k.avatar_emoji}</span> {k.name}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeKid?.kid_id === k.kid_id ? 'bg-white/20' : 'bg-amber-500/10 text-amber-600'}`}>⭐ {k.points_balance || 0}</span>
              </button>
            ))}
            {activeKid && (
              <button onClick={() => { setEditKid(activeKid); setShowProfileDialog(true); }} data-testid="edit-kid-btn"
                className="p-2 rounded-full border border-border text-muted-foreground hover:text-foreground" title="Edit profile">
                <PencilSimple size={14} />
              </button>
            )}
          </div>

          <div className="flex gap-1 bg-muted/50 rounded-xl p-1 w-fit flex-wrap">
            {TABS.map(t => (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`kids-tab-${t.k}`}
                className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${tab === t.k ? 'bg-card shadow-sm' : 'text-muted-foreground'}`}>
                <t.i size={16} /> {t.l}
                {t.k === 'approvals' && pendingCount > 0 && <span className="text-[10px] bg-red-500 text-white rounded-full px-1.5 py-0.5">{pendingCount}</span>}
              </button>
            ))}
          </div>

          {tab === 'overview' && <OverviewTab api={api} kids={kids} activeKid={activeKid} onChanged={fetchKids} />}
          {tab === 'timetable' && activeKid && <TimetableTab api={api} kid={activeKid} />}
          {tab === 'tasks' && activeKid && <TasksTab api={api} kid={activeKid} onChanged={fetchKids} />}
          {tab === 'medicines' && activeKid && <MedsTab api={api} kid={activeKid} />}
          {tab === 'rewards' && <RewardsTab api={api} activeKid={activeKid} />}
          {tab === 'approvals' && <ApprovalsTab api={api} onChanged={fetchKids} />}
        </>
      )}

      {showProfileDialog && (
        <KidProfileDialog api={api} kid={editKid}
          onClose={() => setShowProfileDialog(false)}
          onSaved={() => { setShowProfileDialog(false); fetchKids(); }} />
      )}
    </div>
  );
}
